// ============================================================================
// FLIPCARD COMPONENT - Interactive Loan Payment Card
// ============================================================================
// Working Note: This component creates a flip card interface for loan payments
// that displays loan details on the front and payment form on the back.
// Supports multiple loan types (RD, GOLD, PERS, BIZ, VEH, etc.) with specific validation rules.
// ============================================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Button,
  Stack,
  Badge,
} from "react-bootstrap";
import "../../layouts/Flipcard.css";
import { useDispatch } from "react-redux";
import { MODULEDUES } from "../../services/actions";
import GoldPaymentConfirmationModal from "../../components/GoldPaymentConfirmationModal";
import DepositScheme from "../../components/DepositScheme";

// ============================================================================
// COMPONENT DEFINITION & PROPS
// ============================================================================
const Flipcard = ({
  LoanType,
  loanTypeDesc,
  Accnumber,
  Disbamt,
  Dueamt,
  StartDate,
  MaturityDate,
  onPay,
  RDredemptionDate,
  moduleCode,
  RDinstallmentAmount,
  RDdepositType,


  principalAmountDue, //Principal
  interestAmountDue, //Interest
  penalInterestDue, //Penal
  otherChargesDue,//Others
  suspenseAdvBal, //Less Advance
  EpiAmount, //Installment Amount/RepayInterval .Add RepayInterval at the end of amount
  RepayInterval,
  Instmnt, //Total Installments
  installmentAmountDue, //EPI Amount Due
  insuranceAmountDue, //0
  interestForDelayedInstallment,//Interest OD 
  NextInstallmentDate,

  Loan,
  pledgeNetWeight,

  AvilGateways,
  onMoreClick,
  currentBalance,
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [isFlipped, setIsFlipped] = useState(false);

  const [dueAmount, setDueAmount] = useState(
    moduleCode === "RD" ? RDinstallmentAmount || 0 : Dueamt || 0
  );

  const [validationState, setValidationState] = useState({
    isInvalid: false,
    isMinInvalid: false,
    isGoldAmountInvalid: false,
    isTermAmountInvalid: false,
    message: "",
  });

  const [showGoldConfirmModal, setShowGoldConfirmModal] = useState(false);
  const [showDepositSchemeModal, setShowDepositSchemeModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // ============================================================================
  // HOOKS AND REFS
  // ============================================================================
  const dispatch = useDispatch();
  const cardRef = useRef(null);
  const flipTimeoutRef = useRef(null);

  // ============================================================================
  // CONSTANTS
  // ============================================================================
  const FLIP_TIMEOUT = 20000;
  const MIN_PAYMENT_AMOUNT = 1;
  const SC_MIN_PAYMENT_AMOUNT = 1000;
  const SC_MULTIPLE = 1000;

  const TERM_LOAN_MODULES = useMemo(() => ["PERS", "BIZ", "VEH"], []);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  const getInitialAmount = useCallback(
    () => (moduleCode === "RD" ? RDinstallmentAmount || 0 : Dueamt || 0),
    [moduleCode, RDinstallmentAmount, Dueamt]
  );

  const resetValidation = useCallback(() => {
    setValidationState({
      isInvalid: false,
      isMinInvalid: false,
      isGoldAmountInvalid: false,
      isTermAmountInvalid: false,
      message: "",
    });
  }, []);

  const isTermLoan = useCallback(
    () => TERM_LOAN_MODULES.includes(moduleCode),
    [moduleCode, TERM_LOAN_MODULES]
  );

  // ============================================================================
  // VALIDATION LOGIC
  // ============================================================================
  const validateAmount = useCallback(
    (numericValue) => {
      resetValidation();

      if (numericValue === "") return true;

      // SC module specific validation
      if (moduleCode === "SC") {
        if (numericValue < SC_MIN_PAYMENT_AMOUNT) {
          setValidationState({
            isInvalid: false,
            isMinInvalid: true,
            isGoldAmountInvalid: false,
            isTermAmountInvalid: false,
            message: `Minimum amount of ₹${SC_MIN_PAYMENT_AMOUNT.toLocaleString()} required`,
          });
          return false;
        }

        if (numericValue % SC_MULTIPLE !== 0) {
          setValidationState({
            isInvalid: true,
            isMinInvalid: false,
            isGoldAmountInvalid: false,
            isTermAmountInvalid: false,
            message: `Amount must be a multiple of ₹${SC_MULTIPLE.toLocaleString()}`,
          });
          return false;
        }
      }
      // General minimum amount validation (for non-SC modules)
      else if (numericValue < MIN_PAYMENT_AMOUNT) {
        setValidationState({
          isInvalid: false,
          isMinInvalid: true,
          isGoldAmountInvalid: false,
          isTermAmountInvalid: false,
          message: `Minimum amount of ₹${MIN_PAYMENT_AMOUNT} required`,
        });
        return false;
      }

      if (moduleCode === "RD" && RDinstallmentAmount) {
        const isNotMultiple = numericValue % RDinstallmentAmount !== 0;
        if (isNotMultiple) {
          setValidationState({
            isInvalid: true,
            isMinInvalid: false,
            isGoldAmountInvalid: false,
            isTermAmountInvalid: false,
            message: `Amount must be a multiple of installment amount ₹${RDinstallmentAmount.toLocaleString()}`,
          });
          return false;
        }
      }

      return true;
    },
    [moduleCode, RDinstallmentAmount, resetValidation]
  );

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  const handleFlip = useCallback(() => {
    if (!isProcessing) {
      setIsFlipped((prev) => !prev);
    }
  }, [isProcessing]);

  const handleOutsideClick = useCallback(
    (event) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(event.target) &&
        isFlipped
      ) {
        setIsFlipped(false);
      }
    },
    [isFlipped]
  );

  const handleInputChange = useCallback(
    (event) => {
      event.stopPropagation();
      const value = event.target.value.replace(/^0+|[^0-9]/g, "");
      const numericValue = value ? parseInt(value, 10) : "";
      validateAmount(numericValue);
      setDueAmount(value);
    },
    [validateAmount]
  );

  // ✅ FIXED: Only calls callback, no navigation
  const proceedToPayment = useCallback(() => {
    console.log("═══════════════════════════════════════");
    console.log("💳 [Flipcard] Proceeding to payment");
    console.log("   Amount:", dueAmount);
    console.log("   Account:", Accnumber);
    console.log("   Module:", moduleCode);
    console.log("   Calling Dashboard callback (onPay)...");
    console.log("═══════════════════════════════════════");

    // ✅ ONLY call the callback - Dashboard will handle navigation
    if (onPay && typeof onPay === 'function') {
      onPay(dueAmount);
    } else {
      console.error("❌ [Flipcard] onPay callback not provided!");
    }

  }, [onPay, dueAmount, Accnumber, moduleCode]);

  // ✅ FIXED: Deposit Scheme Handler - Only calls callback
  const handleDepositSchemeConfirm = useCallback((data) => {
    setShowDepositSchemeModal(false);

    console.log("═══════════════════════════════════════");
    console.log("💳 [Flipcard] Deposit scheme confirmed");
    console.log("   Amount:", dueAmount);
    console.log("   Interest Type:", data.interestType);
    console.log("   Calling Dashboard callback with extra data...");
    console.log("═══════════════════════════════════════");

    // ✅ Call Dashboard's onPay with additional data
    if (onPay && typeof onPay === 'function') {
      onPay(dueAmount, {
        interestType: data.interestType,
      });
    } else {
      console.error("❌ [Flipcard] onPay callback not provided!");
    }

  }, [onPay, dueAmount]);

  // ============================================================================
  // PAYMENT PROCESSING
  // ============================================================================
  const handlePayButtonClick = async (event) => {
    event.stopPropagation();

    const minAmount = moduleCode === "SC" ? SC_MIN_PAYMENT_AMOUNT : MIN_PAYMENT_AMOUNT;

    // Pre-validation
    if (isProcessing || !dueAmount || dueAmount < minAmount) return;

    setIsProcessing(true);

    try {
      await new Promise((resolve) => {
        dispatch({
          type: `${MODULEDUES}_POST`,
          payload: {
            uniqueAccountNumber: Accnumber,
            moduleCode: moduleCode,
          },
          callback: (res) => {
            if (res.is_success && res?.data) {
              const paymentAmount = parseInt(dueAmount, 10);

              // Gold loan specific validation
              if (moduleCode === "GOLD" && paymentAmount >= Dueamt) {
                setValidationState({
                  isInvalid: false,
                  isMinInvalid: false,
                  isGoldAmountInvalid: true,
                  isTermAmountInvalid: false,
                  message: `Loan closure is not available online, Maximum allowed payment is less than ₹${Dueamt?.toLocaleString()}. Enter a lower amount.`,
                });
              }
              // Term loan specific validation
              else if (isTermLoan() && paymentAmount > Dueamt) {
                setValidationState({
                  isInvalid: false,
                  isMinInvalid: false,
                  isGoldAmountInvalid: false,
                  isTermAmountInvalid: true,
                  message: `Amount cannot exceed due amount of ₹${Dueamt?.toLocaleString()}`,
                });
              } else {
                // Payment amount is valid - proceed to payment
                proceedToPayment();
              }
            } else {
              setValidationState({
                isInvalid: false,
                isMinInvalid: false,
                isGoldAmountInvalid: false,
                isTermAmountInvalid: true,
                message: "Failed to validate payment. Please try again.",
              });
            }
            setIsProcessing(false);
            resolve();
          },
        });
      });
    } catch (error) {
      console.error("Error processing payment:", error);
      setValidationState({
        isInvalid: false,
        isMinInvalid: false,
        isGoldAmountInvalid: false,
        isTermAmountInvalid: true,
        message: "An error occurred while processing payment. Please try again.",
      });
      setIsProcessing(false);
    }
  };

  // ============================================================================
  // REACT EFFECTS
  // ============================================================================
  useEffect(() => {
    if (isFlipped) {
      flipTimeoutRef.current = setTimeout(() => {
        setIsFlipped(false);
      }, FLIP_TIMEOUT);
    }

    return () => {
      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
      }
    };
  }, [isFlipped]);

  useEffect(() => {
    if (!isFlipped) {
      const initialAmount = getInitialAmount();
      setDueAmount(initialAmount);
      resetValidation();
    }
  }, [isFlipped, getInitialAmount, resetValidation]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [handleOutsideClick]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const isPayDisabled =
    Object.values(validationState).some((val) => val === true) ||
    !dueAmount ||
    isProcessing;

  const {
    isInvalid,
    isMinInvalid,
    isGoldAmountInvalid,
    isTermAmountInvalid,
    message,
  } = validationState;

  // ============================================================================
  // COMPONENT RENDER
  // ============================================================================
  return (
    <div className="d-flex justify-content-center align-items-center vh-10">
      <div
        ref={cardRef}
        className={`credit-card ${isFlipped ? "flipped" : ""}`}
        onClick={handleFlip}
        style={{ cursor: isProcessing ? "not-allowed" : "pointer" }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleFlip();
          }
        }}
        aria-label={
          isFlipped ? "Card back - payment form" : "Card front - loan details"
        }
      >

        {/* CARD FRONT */}
        <div className="card-front">
          <Stack
            direction="horizontal"
            className="position-absolute top-0 end-0 m-2"
            gap={2}
          >
            <Badge bg="info">Tap to pay</Badge>
            {moduleCode && <Badge bg="warning">{moduleCode}</Badge>}
          </Stack>

          <div className="mt-2">

            {loanTypeDesc && (
              <span className="card-brand mt-1"><span>Scheme : </span>{loanTypeDesc}</span>
            )}
            <span>-</span>
            {RDdepositType && (
              <span className="card-brand mt-1">
                {moduleCode} <span></span>
                {RDdepositType}
              </span>
            )}


            <div className="card-text">
              <span className="spaced-text">A/c No:</span>
              {Accnumber}
            </div>
            <div className="card-strip"></div>


            {Disbamt !== null && Disbamt !== undefined && Disbamt !== 0 && (
              <div className="card-text">
                <span>Loan Amount&nbsp;:&nbsp;</span>₹{Disbamt?.toLocaleString()}
              </div>
            )}
            {principalAmountDue !== null && principalAmountDue !== undefined && (
              <div className="card-text">
                <span>Interest and Others&nbsp;:&nbsp;</span>
                {Dueamt - principalAmountDue}
              </div>
            )}
            {Dueamt != null && (
              <div className="card-text-bold">
                <span>Net Due Amount : </span>
                ₹{Number(Dueamt).toLocaleString("en-IN")}
              </div>
            )}

            <div className="card-text">
              {[
                EpiAmount != null && Instmnt != null
                  ? `EPI Amount : ${EpiAmount}/${Instmnt}`
                  : null,

                installmentAmountDue != null
                  ? `EPI Amount Due : ₹${Number(installmentAmountDue).toLocaleString("en-IN")}`
                  : null,
              ]
                .filter(Boolean)
                .map((text, i, arr) => (
                  <span key={i}>
                    {text}
                    {i < arr.length - 1 && (
                      <span style={{ fontWeight: "bold", margin: "0 6px" }}> |</span>
                    )}
                  </span>
                ))}
            </div>




            <div className="card-text">
              {[
                StartDate ? `Start Date: ${StartDate}` : null,
                moduleCode === "RD" && RDredemptionDate
                  ? `Redemption Date: ${RDredemptionDate}`
                  : null,
                MaturityDate ? `Maturity Date: ${MaturityDate}` : null,
              ]
                .filter(Boolean)
                .map((text, i, arr) => (
                  <span key={i}>
                    {text}
                    {i < arr.length - 1 && (
                      <span style={{ fontWeight: "bold", margin: "0 6px" }}>|</span>
                    )}
                  </span>
                ))}
            </div>


            {NextInstallmentDate != null && NextInstallmentDate !== "" && (
              <div className="card-text-bold">
                <span>Next Installment Date : </span>
                {NextInstallmentDate}
              </div>
            )}

            {/* <div className="card-text">
              <span>Open Date &nbsp;:&nbsp;</span>
              {StartDate}
            </div>

            {moduleCode !== "TA" && ((RDredemptionDate && moduleCode === "RD") || MaturityDate) && (
              <div className="card-text">
                <span>
                  {RDredemptionDate && moduleCode === "RD"
                    ? "Redemption"
                    : "Maturity"}
                  Date &nbsp;:&nbsp;
                </span>
                {RDredemptionDate && moduleCode === "RD"
                  ? RDredemptionDate
                  : MaturityDate}
              </div>
            )} */}
          </div>
        </div>

        {/* CARD BACK */}
        <div className="card-back mt-2" >
          <div className="card-back-title">
            Make Payment
            {isProcessing && <span className="ms-2">Processing...</span>}
          </div>
          <div>
            {loanTypeDesc && (
              <span className="card-brand mt-1"><span>Scheme : </span>{loanTypeDesc}</span>
            )}

            {RDdepositType && (
              <span className="card-brand mt-1">
                <span>-</span> {moduleCode} <span></span>
                {RDdepositType}
              </span>
            )}
          </div>
          <div className="card-text">
            <span className="spaced-text">A/c No:</span>
            {Accnumber}
          </div>
          <div className="card-strip"></div>



          {/* <div className="mx-2">

            {principalAmountDue && principalAmountDue !== null && (
              <div className="card-text">
                <span>Principal&nbsp;:&nbsp;</span>
                {principalAmountDue}
              </div>
            )}
            {/* {Disbamt !== null && Disbamt !== undefined && Disbamt !== 0 && (
              <div className="card-text">
                <span>Amount&nbsp;:&nbsp;</span>₹{Disbamt?.toLocaleString()}
              </div>
            )} */}
          {/* {Dueamt > 0 && Dueamt && Dueamt !== undefined && (
              <div className="card-text-bold">
                <span>Total Due Amount : </span>₹{Dueamt?.toLocaleString()}
              </div>
            )} */}

          {/* {interestForDelayedInstallment && interestForDelayedInstallment !== null && interestForDelayedInstallment !== 0 && (
              <div className="card-text">
                <span>Interest OD &nbsp;:&nbsp;</span>
                {interestForDelayedInstallment}
              </div>
            )}
            {interestAmountDue && interestAmountDue !== null && (
              <div className="card-text">
                <span>Interest &nbsp;:&nbsp;</span>
                {interestAmountDue}
              </div>
            )}
            {penalInterestDue && penalInterestDue !== null && penalInterestDue !== 0 && (
              <div className="card-text">
                <span> Penal &nbsp;:&nbsp;</span>
                {penalInterestDue}
              </div>
            )}
            {otherChargesDue && otherChargesDue !== null && (
              <div className="card-text">
                <span>Others &nbsp;:&nbsp;</span>
                {otherChargesDue}
              </div>
            )}
            {suspenseAdvBal && suspenseAdvBal !== null && (
              <div className="card-text">
                <span>Less Advance &nbsp;:&nbsp;</span>
                {suspenseAdvBal}
              </div>
            )}

          </div>  */}

          <div className="mx-2">

            {principalAmountDue != null && (
              <div className="card-text">
                <span>Principal : </span>
                ₹{Number(principalAmountDue).toLocaleString("en-IN")}
              </div>
            )}

            {[
              [
                interestForDelayedInstallment && interestForDelayedInstallment !== 0
                  ? ["Interest OD", interestForDelayedInstallment]
                  : null,
                interestAmountDue != null
                  ? ["Interest", interestAmountDue]
                  : null,
                penalInterestDue && penalInterestDue !== 0
                  ? ["Penal", penalInterestDue]
                  : null,
              ],
              [
                otherChargesDue != null
                  ? ["Others", otherChargesDue]
                  : null,
                suspenseAdvBal != null
                  ? ["Less Advance", suspenseAdvBal]
                  : null,
              ],
            ].map((row, r) => {
              const items = row.filter(Boolean);
              return (
                items.length > 0 && (
                  <div className="card-text" key={r}>
                    {items.map(([label, value], i) => (
                      <span key={i}>
                        {label} : ₹{Number(value).toLocaleString("en-IN")}
                        {i < items.length - 1 && (
                          <span style={{ fontWeight: "bold", margin: "0 6px" }}>|</span>
                        )}
                      </span>
                    ))}
                  </div>
                )
              );
            })}

            {/* </div> */}

            <Stack direction="horizontal" gap={3}>
              <label
                htmlFor="due-amount"
                style={{ marginLeft: "1px", fontSize: "12px", minWidth: "50px" }}
              >
                Amount:
              </label>
              <input
                id="due-amount"
                type="text"
                min="1"
                placeholder="Enter amount"
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/^0+|[^0-9]/g, "");
                }}
                onClick={handleInputChange}
                onChange={handleInputChange}
                value={dueAmount}
                disabled={isProcessing}
                className={`form-control ${isInvalid ||
                  isMinInvalid ||
                  isGoldAmountInvalid ||
                  isTermAmountInvalid
                  ? "is-invalid"
                  : ""
                  }`}
                style={{
                  width: "100%",
                  fontSize: "12px",
                  display: "inline-block",
                  margin: "1px",
                }}
                aria-describedby={message ? "validation-message" : undefined}
              />
              <Button
                variant="success px-4"
                size="small"
                onClick={handlePayButtonClick}
                disabled={isPayDisabled}
                aria-label={`Pay ${dueAmount
                  ? `₹${parseInt(dueAmount).toLocaleString()}`
                  : "amount"
                  }`}
              >
                {isProcessing ? "..." : "Pay"}
              </Button>
            </Stack>
          </div>
          {message && (
            <div
              id="validation-message"
              style={{ color: "red", fontSize: "11px", marginTop: "4px" }}
              role="alert"
              aria-live="polite"
            >
              {message}
            </div>
          )}
        </div>
      </div>

      {/* MODALS */}
      <GoldPaymentConfirmationModal
        show={showGoldConfirmModal}
        onHide={() => {
          setShowGoldConfirmModal(false);
          setIsProcessing(false);
        }}
        onConfirm={() => {
          setShowGoldConfirmModal(false);
          proceedToPayment();
        }}
        amount={dueAmount}
        totalDue={Dueamt}
      />

      <DepositScheme
        show={showDepositSchemeModal}
        onHide={() => setShowDepositSchemeModal(false)}
        onConfirm={handleDepositSchemeConfirm}
        paymentAmount={parseInt(dueAmount, 10)}
        accountNumber={Accnumber}
      />
    </div>
  );
};

export default Flipcard;
